import {
  Model,
  type IIntersection,
  type PickableModel,
} from '@technology/web3d';
import type { Object3D } from '@technology/web3d-plugin-threejs';
import { ISSUE_MARKERS_MODEL_ID } from './issue-marker-sprite.types';

type IssueMarkerCaster = Parameters<PickableModel['pick']>[0];

/** Same priority as `PointIconModel`, so snapped hits and clip-plane handles are not outranked. */
const ISSUE_MARKER_PICK_PRIORITY = 1;

/** Only `PickableModel`, so web3d skips snapped picks, selection, and area selection. */
export class IssueMarkersModel extends Model implements PickableModel {
  constructor() {
    super(ISSUE_MARKERS_MODEL_ID);
  }

  public add(...objects: Object3D[]): void {
    this.root.add(...objects);
    this.updateBoundingBox();
  }

  public remove(...objects: Object3D[]): void {
    this.root.remove(...objects);
    this.updateBoundingBox();
  }

  // An empty box clips distant markers: web3d sizes each pane's far plane from the world box.
  private updateBoundingBox(): void {
    const box = this._boundingBox.value.makeEmpty();
    for (const child of this.root.children) {
      box.expandByPoint(child.position);
    }
    this._boundingBox.next(box);
  }

  public pick(caster: IssueMarkerCaster): Promise<IIntersection> | undefined {
    const hits = caster.intersectObject(this.root, true);
    if (hits.length === 0) {
      return undefined;
    }

    // eslint-disable-next-line sonarjs/reduce-initial-value -- hits is non-empty here
    const nearest = hits.reduce((a, b) =>
      (b.distanceToRay ?? b.distance) < (a.distanceToRay ?? a.distance) ? b : a
    );
    const id = nearest.object.id;
    return Promise.resolve({
      id,
      childrenIds: [id],
      object: nearest.object,
      point: nearest.point,
      distance: nearest.distance,
      distanceToRay: nearest.distanceToRay,
      model: this,
      caster,
      pickPriority: ISSUE_MARKER_PICK_PRIORITY,
    });
  }
}
